import React, { useState, useEffect } from 'react';
import { useProfile } from '../contexts/ProfileContext';
import { getConnections, searchConnectionsByTags, getConnectionStatsByTags } from '../lib/supabase';
import ConnectionDetails from './ConnectionDetails';
import { FiUsers, FiSearch, FiTag, FiX, FiLoader } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import toast from 'react-hot-toast';

const ConnectionsList = () => {
  const { profile } = useProfile();
  const [connections, setConnections] = useState([]);
  const [filteredConnections, setFilteredConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [tagStats, setTagStats] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);

  useEffect(() => {
    if (profile) {
      loadConnections();
      loadTagStats();
    }
  }, [profile]);

  useEffect(() => {
    filterConnections();
  }, [connections, searchTerm, selectedTags]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadConnections = async () => {
    if (!profile) return;

    setLoading(true);
    try {
      const { data, error } = await getConnections(profile.id);
      if (error) {
        throw error;
      }

      setConnections(data || []);
      setFilteredConnections(data || []);

      // Extract all unique tags
      const allTags = new Set();
      (data || []).forEach(conn => {
        if (conn.tags && Array.isArray(conn.tags)) {
          conn.tags.forEach(tag => allTags.add(tag));
        }
      });
      setAvailableTags(Array.from(allTags).sort());
    } catch (error) {
      console.error('Error loading connections:', error);
      toast.error('Failed to load connections');
    } finally {
      setLoading(false);
    }
  };

  const loadTagStats = async () => {
    if (!profile) return;

    try {
      const { data } = await getConnectionStatsByTags(profile.id);
      setTagStats(data || []);
    } catch (error) {
      console.error('Error loading tag stats:', error);
    }
  };

  const filterConnections = () => {
    let filtered = [...connections];

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(conn => {
        const profile1 = conn.profile_1 || {};
        const profile2 = conn.profile_2 || {};
        const otherProfile = profile1.id === profile.id ? profile2 : profile1;
        
        return (
          otherProfile.username?.toLowerCase().includes(term) ||
          otherProfile.display_name?.toLowerCase().includes(term) ||
          conn.notes?.toLowerCase().includes(term) ||
          conn.meeting_context?.event?.toLowerCase().includes(term) ||
          conn.meeting_context?.location?.toLowerCase().includes(term)
        );
      });
    }

    // Filter by tags
    if (selectedTags.length > 0) {
      filtered = filtered.filter(conn => {
        if (!conn.tags || !Array.isArray(conn.tags)) return false;
        return selectedTags.some(tag => conn.tags.includes(tag));
      });
    }

    setFilteredConnections(filtered);
  };

  const toggleTag = (tag) => {
    setSelectedTags(prev =>
      prev.includes(tag)
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  const getOtherProfile = (connection) => {
    const profile1 = connection.profile_1 || {};
    const profile2 = connection.profile_2 || {};
    return profile1.id === profile.id ? profile2 : profile1;
  };

  if (loading) {
    return (
      <div className="glass-dark p-12 rounded-xl border border-dark-700 text-center">
        <SafeIcon icon={FiLoader} className="text-4xl text-lime-500 animate-spin mx-auto mb-4" />
        <p className="text-dark-300">Loading connections...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-lime-bright flex items-center">
            <SafeIcon icon={FiUsers} className="mr-2" />
            Connections ({connections.length})
          </h2>
          <p className="text-dark-400 mt-1">Manage your network</p>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="glass-dark p-4 rounded-xl border border-dark-700 space-y-4">
        {/* Search */}
        <div className="relative">
          <SafeIcon icon={FiSearch} className="absolute left-3 top-3 text-dark-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search connections..."
            className="w-full pl-10 pr-4 py-2 glass rounded-lg text-white placeholder-dark-500 focus:ring-2 focus:ring-lime-500 focus:border-lime-500 border border-dark-700"
          />
        </div>

        {/* Tag Stats and Filters */}
        {tagStats.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-dark-200 mb-2 flex items-center">
              <SafeIcon icon={FiTag} className="mr-2" />
              Filter by Tags
            </label>
            <div className="flex flex-wrap gap-2">
              {tagStats.map(({ tag, count }) => (
                <button
                  key={tag}
                  onClick={() => toggleTag(tag)}
                  className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                    selectedTags.includes(tag)
                      ? 'bg-lime-500/20 text-lime-400 border-lime-500/50'
                      : 'bg-dark-800 text-dark-300 border-dark-700 hover:border-lime-500/50'
                  }`}
                >
                  {tag} ({count})
                </button>
              ))}
            </div>
            {selectedTags.length > 0 && (
              <button
                onClick={() => setSelectedTags([])}
                className="mt-2 text-xs text-dark-400 hover:text-lime-400 inline-flex items-center"
              >
                <SafeIcon icon={FiX} className="mr-1" />
                Clear filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* Connections List */}
      {filteredConnections.length === 0 ? (
        <div className="glass-dark p-12 rounded-xl border border-dark-700 text-center">
          <SafeIcon icon={FiUsers} className="text-4xl text-dark-500 mx-auto mb-4" />
          <p className="text-dark-400">
            {connections.length === 0
              ? 'No connections yet. Start connecting with other HubID users!'
              : 'No connections match your filters.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filteredConnections.map((connection) => {
            const otherProfile = getOtherProfile(connection);
            return (
              <div
                key={connection.id}
                className="glass-dark p-4 rounded-xl border border-dark-700 hover:border-lime-500/50 transition-colors cursor-pointer"
                onClick={() => setSelectedConnection(connection)}
              >
                <div className="flex items-start space-x-4">
                  <img
                    src={otherProfile.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(otherProfile.display_name || otherProfile.username)}&background=84cc16&color=000`}
                    alt={otherProfile.display_name || otherProfile.username}
                    className="w-12 h-12 rounded-full object-cover border-2 border-lime-500/50"
                  />
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-dark-100 truncate">
                      {otherProfile.display_name || otherProfile.username}
                    </h3>
                    <p className="text-sm text-dark-400 truncate">
                      @{otherProfile.username}
                    </p>
                    {connection.tags && connection.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {connection.tags.slice(0, 3).map((tag) => (
                          <span
                            key={tag}
                            className="px-2 py-0.5 bg-lime-500/20 text-lime-400 rounded text-xs border border-lime-500/50"
                          >
                            {tag}
                          </span>
                        ))}
                        {connection.tags.length > 3 && (
                          <span className="px-2 py-0.5 text-dark-400 rounded text-xs">
                            +{connection.tags.length - 3}
                          </span>
                        )}
                      </div>
                    )}
                    {connection.notes && (
                      <p className="text-xs text-dark-400 mt-2 line-clamp-2">
                        {connection.notes}
                      </p>
                    )}
                    {connection.meeting_context?.event && (
                      <p className="text-xs text-dark-500 mt-1">
                        Met at: {connection.meeting_context.event}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Connection Details Modal */}
      {selectedConnection && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
          <div className="glass-dark rounded-xl border border-dark-700 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-lime-bright">Connection Details</h3>
                <button
                  onClick={() => setSelectedConnection(null)}
                  className="text-dark-400 hover:text-white transition-colors"
                >
                  <SafeIcon icon={FiX} className="text-2xl" />
                </button>
              </div>
              <ConnectionDetails
                connection={selectedConnection}
                onUpdate={() => {
                  loadConnections();
                  loadTagStats();
                  setSelectedConnection(null);
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConnectionsList;

